import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useModalContext } from '@/components/ui/modal-provider';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { useDeleteAnamnesis } from '@/hooks/use-patient-anamnesis';
import { PatientAnamnesisPage } from '@/pages/constants';
import AnamnesisStartModal from './AnamnesisStartModal';
import type { AnamnesisTemplateId } from './templates';
import type { Patient } from '@/types/patient';

/**
 * Where the anamnesis editor lives, plus the state that tells it how to start.
 * Exported because the modal layer renders outside the router — callers there
 * navigate with their own (router-scoped) `navigate`.
 */
export const anamnesisEditorRoute = (
    patientId: string,
    templateId?: AnamnesisTemplateId,
    replaceExisting = false,
) => ({
    pathname: `/${generatePath(PatientAnamnesisPage, { patientId })}`,
    state: templateId ? { templateId, replaceExisting } : undefined,
});

/**
 * Centralizes the entry-point flows for a patient's anamnesis (choose how to
 * start, open the editor, delete), shared by the patient page's Anamnese tab,
 * the hero status pill and the global "Nova anamnese" wizard. The patient
 * record is optional but lets the start step suggest the age-appropriate
 * template.
 */
export function useAnamnesisActions(patientId?: string, patient?: Patient) {
    const navigate = useNavigate();
    const { openWizardModal, closeModal } = useModalContext();
    const confirmDelete = useConfirmDelete();
    const deleteMutation = useDeleteAnamnesis(patientId);

    const open = (templateId?: AnamnesisTemplateId, replaceExisting = false) => {
        if (!patientId) return;
        const { pathname, state } = anamnesisEditorRoute(patientId, templateId, replaceExisting);
        navigate(pathname, { state });
    };

    // `create` is wired straight to onClick handlers in a few places, so the
    // replace flag lives on this internal helper instead of on its signature.
    const openStartStep = (replaceExisting: boolean) => {
        if (!patientId) return;
        // `openWizardModal` strips the body padding so the layout below can own
        // the whole dialog — header (title, patient, back) included.
        openWizardModal({
            size: 'lg',
            content: (
                <AnamnesisStartModal
                    patientId={patientId}
                    patient={patient}
                    onSelect={(templateId) => {
                        closeModal();
                        open(templateId, replaceExisting);
                    }}
                    onUploaded={() => {
                        closeModal();
                        open();
                    }}
                />
            ),
        });
    };

    const create = () => openStartStep(false);

    const remove = (onDeleted?: () => void) => {
        confirmDelete({
            title: 'Excluir anamnese?',
            description:
                'A anamnese deste paciente será excluída permanentemente. Essa ação não pode ser desfeita.',
            confirmText: 'Sim, excluir',
            onConfirm: async (close) => {
                try {
                    await deleteMutation.mutateAsync();
                    toast.success('Anamnese excluída');
                    close();
                    onDeleted?.();
                } catch {
                    toast.error('Não foi possível excluir a anamnese');
                }
            },
        });
    };

    return { open, create, remove };
}
